import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'
import NProgress from 'nprogress/nprogress'
import store from '@state/store'
import { updateNotificationSubscription } from '@src/api/notifications'
import { isMobileBrowser, saveDataTracking } from '@src/api/tracking'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return {
        x: 0,
        y: 0,
      }
    }
  },
})

function clearOnboardingLoginHandoff() {
  document.body.classList.remove('onboarding-login-handoff')
}

function shouldKeepOnboardingLoginHandoff(routeTo) {
  if (routeTo.name === 'onboarding-welcome') {
    return true
  }

  return routeTo.name === 'onboarding' && store.getters['onboarding/shouldShowWelcomeOverlay']
}

router.beforeEach((routeTo, routeFrom, next) => {
  if (routeFrom.name !== null) {
    NProgress.start()
  }

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired)

  if (!authRequired) {
    return next()
  }

  if (store.getters['auth/loggedIn']) {
    return store
      .dispatch('auth/validate')
      .then((validUser) => {
        if (validUser) {
          loadUser()
          return
        }

        redirectToLogin()
      })
      .catch(() => {
        redirectToLogin()
      })
  }

  redirectToLogin()

  function redirectToLogin() {
    if (window.location.pathname.length > 1) {
      next({
        name: 'login',
        query: { returnUrl: window.location.pathname + window.location.search },
      })
      return
    }
    next({ name: 'login' })
  }

  function loadUser() {
    if (store.getters['user/currentUser'].id === undefined) {
      store.dispatch('user/getMyUser').then(() => {
        next()
      })
    } else {
      next()
    }
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

router.onError(() => {
  clearOnboardingLoginHandoff()
})

router.afterEach((routeTo, routeFrom) => {
  NProgress.done()
  if (!shouldKeepOnboardingLoginHandoff(routeTo)) {
    clearOnboardingLoginHandoff()
  }
  if (routeTo.name === 'logout') {
    router.push({ name: 'login' })
  }
  if (localStorage.subscription && routeTo.name !== 'login') {
    const obj = JSON.parse(localStorage.subscription)
    if (!obj) {
      return
    }
    const data = {
      type: isMobileBrowser() ? 'MOBILE' : 'WEB',
      endpoint: obj.endpoint,
      deviceName: navigator.userAgent,
      keyP256dh: obj.keys.p256dh,
      keyAuth: obj.keys.auth,
    }
    localStorage.removeItem('subscription')
    updateNotificationSubscription(data)
  }
  if (localStorage.successfulLogin) {
    localStorage.removeItem('successfulLogin')
    afterLoginTracking()
  }

  async function afterLoginTracking() {
    try {
      await saveDataTracking('LOGIN')
    } catch (e) {}
  }
})

export default router
